import { useEffect, useRef, useState } from 'react';

/** Flips `visible` to true once the element scrolls into view, and stays
 * true thereafter. Skips the observer entirely under reduced motion, since
 * the .reveal CSS class is already a no-op (fully visible) in that case. */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }

    // Content already in view on mount (e.g. the hero) shouldn't sit
    // invisible waiting on the observer's first callback — show it right
    // away and only use the observer for what's actually off-screen.
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

export default useScrollReveal;
